import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Heading } from '@abyss/web/ui/Heading';
import { Icon } from '@abyss/web/ui/Icon';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { Text } from '@abyss/web/ui/Text';
import React, { useState } from 'react'; // Update this line
import { useTranslation } from 'react-i18next';

import { useHsidUuid } from '../../../hooks/useHsidUuid';
import { useProviderBioFeedback } from '../../../hooks/useProviderBioFeedback';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import { h2SmallerFontForResponsive, phoneOnly } from '../../ConstantsStyles';
import { SectionContainer } from '../../ProviderDetailTabs/ProviderLocationsDetails/ProviderLocation.style';
import { getProviderBioFeedback, setProviderBioFeedback } from '../Utils';
import { AiSvg } from './AiSvg';
import {
  DisclaimerContainer,
  StyledGeneratedAIText,
  textContainer,
} from './FacilityDetailsSummary.styled';

type FacilityDetailsSummaryProps = {
  facilityTitle: string;
  facilityBio: string;
  npi?: string;
};
export const FacilityDetailsSummary = ({
  facilityTitle,
  facilityBio,
  npi,
}: FacilityDetailsSummaryProps) => {
  const { t } = useTranslation();
  const isWidget = useStore(StoreKeys.IS_WIDGET);
  const phoneScreen = useMediaQuery(phoneOnly) || isWidget;
  const message = t('BIOGRAPHY_SECTION_AI.THANK_YOU_MESSAGE');
  const [feedbackState, setFeedbackState] = useState<{
    submitted: boolean;
    message: string;
  }>({
    submitted: false,
    message: '',
  });

  const [, submitFeedback] = useProviderBioFeedback({
    onSuccess: (data) => {
      setFeedbackState({
        submitted: true,
        message: data?.message || message,
      });
    },
  });

  const getBiographyContent = (content, phoneScreen) => (
    <Text
      css={{ ...textContainer(phoneScreen) }}
      dangerouslySetInnerHTML={{
        __html: content || t('BIOGRAPHY_SECTION.BIOGRAPHY_NOT_AVAILABLE'),
      }}
      data-auto-testid="ai-biography-text"
      data-testid="ai-biography-text"
    />
  );

  const hsidUuid = useHsidUuid();
  const handleFeedback = (isPositive: boolean) => {
    if (!feedbackState.submitted && npi) {
      // Immediately show thank you message
      setFeedbackState({
        submitted: true,
        message,
      });

      // Submit feedback to API (fire and forget)
      submitFeedback({
        variables: {
          input: {
            npi,
            thumbsUp: isPositive,
            userId: hsidUuid,
          },
        },
      });

      // Save feedback to local storage
      setProviderBioFeedback(npi, isPositive);
    }
  };

  React.useEffect(() => {
    if (npi) {
      const feedback = getProviderBioFeedback(npi);
      if (typeof feedback === 'boolean') {
        setFeedbackState({
          submitted: true,
          message,
        });
      }
    }
  }, [npi, message]);

  return (
    <SectionContainer>
      <Heading
        css={h2SmallerFontForResponsive}
        data-auto-testid="ai-biography-title"
        data-testid="ai-biography-title"
        display="h4"
        offset={1}
      >
        {facilityTitle}
      </Heading>
      <DisclaimerContainer>
        <Icon size="$sm" title="AI">
          {AiSvg}
        </Icon>
        <StyledGeneratedAIText data-testid="generated-ai-text">
          {t('BIOGRAPHY_SECTION_AI.AI_SUMMARY_DISCLAIMER')}
        </StyledGeneratedAIText>
      </DisclaimerContainer>
      {getBiographyContent(facilityBio, phoneScreen)}
      {!feedbackState.submitted ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginTop: '16px',
          }}
        >
          <IconSymbol
            aria-label="Thumbs up"
            color="$primary1"
            data-testid="thumbs-up-button"
            icon="thumb_up"
            onClick={() => handleFeedback(true)}
            style={{
              cursor: 'pointer',
            }}
            variant="outlined"
          />
          <IconSymbol
            aria-label="Thumbs down"
            color="$primary1"
            data-testid="thumbs-down-button"
            icon="thumb_down"
            onClick={() => handleFeedback(false)}
            style={{
              cursor: 'pointer',
            }}
            variant="outlined"
          />
          <Text>{t('BIOGRAPHY_SECTION_AI.FEEDBACK_QUESTION')}</Text>
        </div>
      ) : (
        <div style={{ marginTop: '16px' }}>
          <Text>{feedbackState.message || message}</Text>
        </div>
      )}
    </SectionContainer>
  );
};
